/**
 * A Prisma `Decimal` column's shape on the wire: it always serializes as a
 * string over JSON, never as a real `number`. Response types use this
 * instead of `number` so a Decimal-backed field says what it actually
 * receives — `toNum`/`toNumOrNull` are the only place that converts it.
 */
export type DecimalString = string;

/**
 * Converts a Prisma `Decimal` field, which arrives over JSON as a string,
 * to a number before any arithmetic. `null`, `undefined` and a non-numeric
 * string give 0.
 */
export function toNum(value: DecimalString | number | null | undefined): number {
  if (value === null || value === undefined) return 0;
  const num = typeof value === 'string' ? parseFloat(value) : value;
  return Number.isNaN(num) ? 0 : num;
}

/**
 * Same conversion as `toNum`, but keeps `null` instead of collapsing it to
 * 0 — for a nullable Decimal field where the UI must show "—" rather than
 * a false zero (e.g. a session not yet closed, a discount never applied).
 * `undefined` and a non-numeric string still give `null`.
 */
export function toNumOrNull(value: DecimalString | number | null | undefined): number | null {
  if (value === null || value === undefined) return null;
  const num = typeof value === 'string' ? parseFloat(value) : value;
  return Number.isNaN(num) ? null : num;
}
